import { useParams } from 'react-router-dom'
import React, {useEffect, useState} from "react";
import { db } from "../../services/firebase";
import {getDoc, doc} from "firebase/firestore";
import Snackbar from "@mui/material/Snackbar";
import {
    Alert, Autocomplete, Button, CircularProgress,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    TextField
} from "@mui/material";
import {Controller, useForm} from "react-hook-form";
import {
    addMonthsToISODate,
    endpoint,
    provincieItaliane,
    required_error,
    standardLeadFormSchema,
} from "../../services/shared";
import { ConfigLP, ProfessioneEnum } from "../../types";
import {useAuth} from "../../providers/AuthProvider";
import {zodResolver} from "@hookform/resolvers/zod";
import axios, {AxiosError} from "axios";
import {z} from "zod";
import {apiClient} from "../../services/api";

const formatToISODate = (date?: string): string | null => {
    if (!date) return null;

    const [day, month, year] = date.split("/");
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
};
const calculateData40 = (
    decorrenzaISO?: string,
    durata?: number
): string | null => {
    if (!decorrenzaISO || !durata) return null;

    const months40 = Math.floor(durata * 0.4);

    return addMonthsToISODate(decorrenzaISO, months40);
};

type FormData = z.infer<typeof formSchema>;
const formSchema = standardLeadFormSchema.extend({
    decorrenza: z
        .string()
        .optional()
        .refine(
            (val) => !val || /^\d{2}\/\d{2}\/\d{4}$/.test(val),
            { message: "Formato data inizio non valido" }
        )
        .refine(
            (val) => {
                if (!val) return true;

                const [day, month, year] = val.split("/").map(Number);
                const inputDate = new Date(year, month - 1, day);
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                return inputDate <= today;
            },
            { message: "La data di inizio non può essere futura" }
        ),

    rata: z.coerce
        .number()
        .positive("La rata deve essere maggiore di zero").optional(),

    durata: z.coerce
        .number()
        .int("La durata deve essere un numero intero")
        .min(24, "Durata minima 24 mesi")
        .max(120, "Durata massima 120 mesi").optional(),
});

export function LpStandalone() {
    const {hash} = useParams();
    const { freshToken } = useAuth();
    const [loading, setLoading] = useState(true)
    const [config, setConfig] = useState<ConfigLP>()

    const [snackState, setSnackState] = useState<boolean>(false);
    const [snackMessage, setSnackMessage] = useState<{
        severity: any;
        message: string;
    }>({
        severity: "success",
        message: "",
    });
    const [importoDisplay, setImportoDisplay] = useState("");
    const [importoRataDisplay, setImportoRataDisplay] = useState("");
    const formatToCurrency = (val: string) => {
        const cleaned = val.replace(/\D/g, "");
        if (!cleaned) return "";
        const number = parseFloat(cleaned) / 100;
        return number.toLocaleString("it-IT", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    };

    const form = useForm<FormData>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            note: "",
            importo: undefined,
            rata: undefined,
            nome: "",
            cognome: "",
            email: "",
            cellulare: "",
            tipologiaRichiesta: "Cessione del Quinto",
            distributionChannel: "hub"
        }
    });
    const {
        control,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting },
    } = form;

    const onSubmit = handleSubmit(async (data) => {
        let res = null;
        const freshTK = await freshToken();

        const date = new Date();
        const y = date.getFullYear()
        const m = String(date.getMonth() + 1).padStart(2, '0')
        const d = String(date.getDate()).padStart(2, '0')

        const today = `${y}-${m}-${d}`;

        const decorrenzaISO = formatToISODate(data.decorrenza);
        const dataNascita = formatToISODate(data.dataNascita);

        const payload = {
            ...data,
            ...config,
            dataNascita,
            c__mjslq774: today,
            tipologiaRichiesta: "Cessione del Quinto",
            distributionChannel: "hub",
            c__p4ft2h15: "Cessione del Quinto",

            ...(decorrenzaISO && {
                decorrenza: decorrenzaISO,
            }),

            ...(decorrenzaISO && data.durata && {
                dataFine: addMonthsToISODate(decorrenzaISO, data.durata),
                data40: calculateData40(decorrenzaISO, data.durata),
            }),
        };

        console.log({payload});
        try {
            res = await apiClient.post(
                `/v1/leads/sender-standalone`,
                payload,
                {
                    headers: {
                        "Content-Type": `application/json`,
                    },
                },
            );
        } catch (e) {
            const errorMessage = e instanceof AxiosError ? e?.response?.data.error : String(e);
            console.error("Errore durante il salvataggio", errorMessage);
            setSnackMessage({
                severity: "error",
                message: "Errore durante il salvataggio",
            });
            setSnackState(true);
        }
        if (res?.status === 200) {
            setSnackMessage({ severity: "success", message: "Lead inviato" });
            setSnackState(true);
            reset();
            setImportoDisplay(""); // 🔑 QUESTO
            setImportoRataDisplay(""); // 🔑 QUESTO
        }
    });

    async function getLp(hash: string) {
        const docRef = doc(db, `lp/${hash}`);
        const snap = await getDoc(docRef);

        if (!snap.exists()) {
            setSnackMessage({severity: "warning", message: "Errore nel recupero dati"});
            setSnackState(true);
            console.error("Errore nel recupero dati");
        }
        return JSON.parse(snap.data()?.config) as ConfigLP
    }

    useEffect(() => {
        if (hash) {
            getLp(hash)
                .then((data) => {
                    console.log({lpConfig: data, hash});
                    setConfig(data);
                })
                .finally(() => setLoading(false))
        }
    }, [hash])

    return <div className={'container'}>
        {/*
        ------------------------
        Landing PAGE
        ------------------------
        */}
        <h2 style={{
            marginTop: "1em",
            marginBottom: "1em",
        }}>
            📍Inserisci lead
        </h2>
        <form
            onSubmit={onSubmit}
            style={{
                background: "white",
                padding: "1em",
                borderRadius: "1em"
            }}
            className="flex-col gap-2"
        >
            <div className={"flex-col gap-2"}>
                {/*<div className="flex-row gap-2">*/}
                {/*    <Controller*/}
                {/*        name="tipologiaRichiesta"*/}
                {/*        control={control}*/}
                {/*        render={({field}) => (*/}
                {/*            <div style={{width: "100%", maxWidth: "195px"}}>*/}
                {/*                <FormControl fullWidth size="small">*/}
                {/*                    <InputLabel id="tipologia-label">*/}
                {/*                        Tipologia richiesta*/}
                {/*                    </InputLabel>*/}
                {/*                    <Select*/}
                {/*                        {...field}*/}
                {/*                        style={{textAlign: "left"}}*/}
                {/*                        labelId="tipologia-label"*/}
                {/*                        value={field.value || ""}*/}
                {/*                        error={!!(errors as any).tipologiaRichiesta}*/}
                {/*                    >*/}
                {/*                        <MenuItem value="" disabled>*/}
                {/*                            Seleziona una opzione*/}
                {/*                        </MenuItem>*/}
                {/*                        <MenuItem value="Cessione del Quinto">Cessione</MenuItem>*/}
                {/*                        <MenuItem value="Mutuo Immobile">Mutuo</MenuItem>*/}
                {/*                    </Select>*/}
                {/*                    {(errors as any).tipologiaRichiesta && (*/}
                {/*                        <FormHelperText style={{color: "#d32f2f"}}>*/}
                {/*                            {(errors as any).tipologiaRichiesta.message}*/}
                {/*                        </FormHelperText>*/}
                {/*                    )}*/}
                {/*                </FormControl>*/}
                {/*            </div>*/}
                {/*        )}*/}
                {/*    />*/}
                {/*    <Controller*/}
                {/*        name="distributionChannel"*/}
                {/*        control={control}*/}
                {/*        render={({field}) => (*/}
                {/*            <div style={{width: "100%", maxWidth: "195px"}}>*/}
                {/*                <FormControl fullWidth size="small">*/}
                {/*                    <InputLabel id="canale-smist">*/}
                {/*                        Canale destinazione*/}
                {/*                    </InputLabel>*/}
                {/*                    <Select*/}
                {/*                        {...field}*/}
                {/*                        style={{textAlign: "left"}}*/}
                {/*                        labelId="canale-smist"*/}
                {/*                        value={field.value || ""}*/}
                {/*                        error={!!(errors as any).distributionChannel}*/}
                {/*                    >*/}
                {/*                        <MenuItem value="" disabled>*/}
                {/*                            Seleziona una opzione*/}
                {/*                        </MenuItem>*/}
                {/*                        <MenuItem value="hub">HUB</MenuItem>*/}
                {/*                        <MenuItem value="rete">Rete</MenuItem>*/}
                {/*                    </Select>*/}
                {/*                    {(errors as any).distributionChannel && (*/}
                {/*                        <FormHelperText style={{color: "#d32f2f"}}>*/}
                {/*                            {(errors as any).distributionChannel.message}*/}
                {/*                        </FormHelperText>*/}
                {/*                    )}*/}
                {/*                </FormControl>*/}
                {/*            </div>*/}
                {/*        )}*/}
                {/*    />*/}
                {/*</div>*/}
                <div className="flex-row gap-2">
                    <Controller
                        name="nome"
                        control={control}
                        render={({field}) => (
                            <TextField
                                {...field}
                                fullWidth
                                size="small"
                                label="Nome"
                                placeholder="Es. Massimo"
                                value={field.value ?? ""}
                                error={!!errors.nome}
                                helperText={errors.nome?.message}
                            />
                        )}
                    />
                    <Controller
                        name="cognome"
                        control={control}
                        render={({field}) => (
                            <TextField
                                {...field}
                                fullWidth
                                size="small"
                                label="Cognome"
                                placeholder="Es. Rossi"
                                value={field.value ?? ""}
                                error={!!errors.cognome}
                                helperText={errors.cognome?.message}
                            />
                        )}
                    />
                </div>
                <div className="flex-row gap-2">
                    <Controller
                        name="importo"
                        control={control}
                        render={({field}) => (
                            <TextField
                                size="small"
                                label="Importo"
                                placeholder="Es. 10.000,00"
                                value={importoDisplay}
                                onChange={(e) => {
                                    const input = e.target.value;
                                    const cleaned = input.replace(/\D/g, "");
                                    const number = parseFloat(cleaned) / 100;
                                    setImportoDisplay(formatToCurrency(input));
                                    field.onChange(isNaN(number) ? undefined : number);
                                }}
                                error={!!(errors as any).importo}
                                helperText={(errors as any).importo?.message}
                            />
                        )}
                    />
                    <Controller
                        name="provincia"
                        control={control}
                        render={({field}) => (
                            <Autocomplete
                                size="small"
                                disablePortal
                                options={provincieItaliane}
                                sx={{width: 300}}
                                value={field.value || null}
                                onChange={(_, newValue) => field.onChange(newValue)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Provincia"
                                        error={!!(errors as any).provincia}
                                        helperText={(errors as any).provincia?.message}
                                    />
                                )}
                            />
                        )}
                    />
                </div>
                <div className="flex-row gap-2">
                    <Controller
                        name="sesso"
                        control={control}
                        render={({field}) => (
                            <div style={{width: "50%", maxWidth: "195px"}}>
                                <FormControl fullWidth size="small">
                                    <InputLabel id="sesso-label">Seleziona sesso</InputLabel>
                                    <Select
                                        {...field}
                                        style={{textAlign: "left"}}
                                        labelId="sesso-label"
                                        value={field.value || ""}
                                        error={!!(errors as any).sesso}
                                    >
                                        <MenuItem value="" disabled>
                                            Seleziona una opzione
                                        </MenuItem>
                                        <MenuItem value="F">Donna</MenuItem>
                                        <MenuItem value="M">Uomo</MenuItem>
                                    </Select>
                                    {(errors as any).sesso && (
                                        <FormHelperText style={{color: "#d32f2f"}}>
                                            {(errors as any).sesso.message}
                                        </FormHelperText>
                                    )}
                                </FormControl>
                            </div>
                        )}
                    />
                    <Controller
                        name="dataNascita"
                        control={control}
                        render={({field}) => (
                            <TextField
                                {...field}
                                size="small"
                                label="Data di nascita"
                                placeholder="Es. 11/08/1987"
                                value={field.value ?? ""}
                                error={!!(errors as any).dataNascita}
                                helperText={(errors as any).dataNascita?.message}
                                onChange={(e) => {
                                    let value = e.target.value.replace(/\D/g, ""); // rimuove tutto tranne numeri
                                    if (value.length >= 3 && value.length <= 4)
                                        value = value.replace(/^(\d{2})(\d{1,2})/, "$1/$2");
                                    else if (value.length > 4)
                                        value = value.replace(
                                            /^(\d{2})(\d{2})(\d{1,4}).*/,
                                            "$1/$2/$3",
                                        );

                                    field.onChange(value);
                                }}
                            />
                        )}
                    />
                </div>
                <div className="flex-row gap-2">
                    <Controller
                        name="cellulare"
                        control={control}
                        render={({field}) => (
                            <TextField
                                {...field}
                                size="small"
                                label="Cellulare"
                                value={field.value || ""}
                                placeholder="Inserisci cellulare"
                                error={!!errors.cellulare}
                                helperText={errors.cellulare?.message}
                            />
                        )}
                    />
                    <Controller
                        name="email"
                        control={control}
                        render={({field}) => (
                            <TextField
                                {...field}
                                fullWidth
                                size="small"
                                label="Email"
                                value={field.value || ""}
                                placeholder="Inserisci email"
                                error={!!(errors as any).email}
                                helperText={(errors as any).email?.message}
                            />
                        )}
                    />
                </div>
                <div className="flex-row gap-2">
                    <Controller
                        name="nomeAtc"
                        control={control}
                        render={({field}) => (
                            <TextField
                                {...field}
                                size="small"
                                label="Nome ATC"
                                placeholder="Inserisci nome ATC"
                                value={field.value || ""}
                                error={!!(errors as any).nomeAtc}
                                helperText={(errors as any).nomeAtc?.message}
                            />
                        )}
                    />
                    <Controller
                        name="pIva"
                        control={control}
                        render={({field}) => (
                            <TextField
                                {...field}
                                size="small"
                                label="Partita IVA"
                                placeholder="Inserisci P.IVA"
                                value={field.value || ""}
                                error={!!(errors as any).pIva}
                                helperText={(errors as any).pIva?.message}
                            />
                        )}
                    />
                </div>
                <div className="flex-row gap-2">
                    <Controller
                        name="rating"
                        control={control}
                        render={({field}) => (
                            <TextField
                                {...field}
                                size="small"
                                label="Valutazione ATC"
                                placeholder="Inserisci valutazione"
                                value={field.value || ""}
                                error={!!(errors as any).rating}
                                helperText={(errors as any).rating?.message}
                            />
                        )}
                    />
                    <Controller
                        name="professione"
                        control={control}
                        render={({field}) => (
                            <FormControl fullWidth size="small">
                                <InputLabel id="professione-label">
                                    Seleziona professione
                                </InputLabel>
                                <Select
                                    {...field}
                                    style={{textAlign: "left"}}
                                    labelId="professione-label"
                                    value={field.value || ""}
                                    error={!!(errors as any).professione}
                                >
                                    <MenuItem value="" disabled>
                                        Seleziona una opzione
                                    </MenuItem>
                                    {Object.entries(ProfessioneEnum).map(([key, value]) => (
                                        <MenuItem key={key} value={value}>
                                            {value}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {(errors as any).professione && (
                                    <FormHelperText style={{color: "#d32f2f"}}>
                                        {(errors as any).professione.message}
                                    </FormHelperText>
                                )}
                            </FormControl>
                        )}
                    />
                </div>
                <div className={"flex-row gap-2"}>
                    <Controller
                        name="decorrenza"
                        control={control}
                        render={({field}) => (
                            <TextField
                                {...field}
                                size="small"
                                label="Data inizio"
                                placeholder="Es. 01/01/2022"
                                value={field.value ?? ""}
                                error={!!(errors as any).decorrenza}
                                helperText={(errors as any).decorrenza?.message}
                                onChange={(e) => {
                                    let value = e.target.value.replace(/\D/g, ""); // rimuove tutto tranne numeri
                                    if (value.length >= 3 && value.length <= 4)
                                        value = value.replace(/^(\d{2})(\d{1,2})/, "$1/$2");
                                    else if (value.length > 4)
                                        value = value.replace(
                                            /^(\d{2})(\d{2})(\d{1,4}).*/,
                                            "$1/$2/$3",
                                        );

                                    field.onChange(value);
                                }}
                            />
                        )}
                    />
                    <Controller
                        name="rata"
                        control={control}
                        render={({field}) => (
                            <TextField
                                size="small"
                                label="Rata"
                                placeholder="Es. 220,00"
                                value={importoRataDisplay}
                                onChange={(e) => {
                                    const input = e.target.value;
                                    const cleaned = input.replace(/\D/g, "");
                                    const number = parseFloat(cleaned) / 100;
                                    setImportoRataDisplay(formatToCurrency(input));
                                    field.onChange(isNaN(number) ? undefined : number);
                                }}
                                error={!!(errors as any).rata}
                                helperText={(errors as any).rata?.message}
                            />
                        )}
                    />
                    <Controller
                        name="durata"
                        control={control}
                        render={({field}) => (
                            <TextField
                                size="small"
                                label="Durata (mesi)"
                                placeholder="Es. 60"
                                value={field.value ?? ""}
                                onChange={(e) => {
                                    const input = e.target.value;
                                    const cleaned = input.replace(/\D/g, "");
                                    const number = cleaned ? parseInt(cleaned, 10) : undefined;

                                    field.onChange(number);
                                }}
                                error={!!(errors as any).durata}
                                helperText={(errors as any).durata?.message}
                                inputProps={{
                                    inputMode: "numeric",
                                    min: 24,
                                    max: 120,
                                }}
                            />
                        )}
                    />
                </div>

                <Controller
                    name="note"
                    control={control}
                    render={({field}) => (
                        <TextField
                            {...field}
                            label="Note"
                            multiline
                            rows={4}
                            placeholder="Scrivi qualcosa..."
                            fullWidth
                            error={!!(errors as any).note}
                            helperText={(errors as any).note?.message}
                        />
                    )}
                />
            </div>
            <Button onClick={() => {
                reset();
                setImportoDisplay("");
            }}>Annulla</Button>
            <Button variant={"contained"} type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                    <>
                        Elaboro richiesta&nbsp;
                        <CircularProgress size={16} color="inherit"/>
                    </>
                ) : (
                    "Conferma"
                )}
            </Button>
        </form>
        {/*
        ------------------------
        GESTIONE MESSAGGI UTENTE
        ------------------------
        */}
        <Snackbar
            anchorOrigin={{vertical: "bottom", horizontal: "center"}}
            open={snackState}
            onClose={() => setSnackState(false)}
            autoHideDuration={3000}
        >
            <Alert
                onClose={() => setSnackState(false)}
                severity={snackMessage.severity}
                variant="filled"
                sx={{width: "100%"}}
            >
                {snackMessage.message}
            </Alert>
        </Snackbar>
    </div>
}
